import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  faCirclePlus,
  faImage,
  faLock,
  faLockOpen,
} from "@fortawesome/free-solid-svg-icons";
import {
  IAppDashboardMenu,
  IMenuItemPermissions,
  IMenuItem,
  PluginInjectorApplicationPlugin,
  PluginInjectorService,
  WidgetDataStore,
} from "core-client/api";
import { BehaviorSubject, Observable, combineLatest, map } from "rxjs";
import { PLUGIN_MODULE_PERMISSIONS } from "../shared/permissions.constant";

@Injectable({
  providedIn: "root",
})
export class WidgetControlService {
  private navbarMenuItemLock!: IMenuItem;
  private navbarMenuItemAdd!: IMenuItem;
  private readonly pluginModuleTitle: string = "WIDGET";
  private readonly navbarMenuTitleLock: string = "WIDGET_LOCK";
  private readonly navbarMenuTitleAdd: string = "WIDGET_ADD";
  private menuItemPermissions: IMenuItemPermissions;

  private readonly widgetAddDisabled!: Observable<boolean>;

  constructor(
    private readonly pluginInjectorService: PluginInjectorService,
    private readonly widgetDataStore: WidgetDataStore,
  ) {
    this.menuItemPermissions = {
      widget: JSON.parse(JSON.stringify(PLUGIN_MODULE_PERMISSIONS.widget)),
    };

    this.widgetAddDisabled = combineLatest([
      this.widgetDataStore.edit$,
    ]).pipe(map(([active]) => !active));
  }

  getNavbarMenuItem(
    pluginConfig: PluginInjectorApplicationPlugin.IApplicationPlugin,
    dashboardMenu: IMenuItem[],
    primaryEnabled: boolean,
    secondaryEnabled: boolean,
  ): IMenuItem[] {
    const menuItemsPermissionsRefs = this.getMenuItemPermissionRefs();

    // WIDGET_ADD
    this.navbarMenuItemAdd = {
      pluginId: pluginConfig._id,
      pluginName: pluginConfig.name,
      pluginModule: this.pluginModuleTitle,
      nameIdentifier: this.navbarMenuTitleAdd,
      accessRights: menuItemsPermissionsRefs,
      primary: {
        enabled: primaryEnabled,
        position: dashboardMenu.length + 1,
      },
      secondary: {
        enabled: secondaryEnabled,
        position: dashboardMenu.length + 1,
      },
      strictMode: true,
      headerSortOrder: 20,
      iconLayers: [
        { icon: faImage },
        { icon: faCirclePlus, transform: "shrink-6 right-8 down-8" },
      ],
      dropdown: false,
      disabled: this.widgetAddDisabled,
      callback: async (dialog: MatDialog, id?: string) => {
        await this.openMenuDialog(pluginConfig._id, dialog, id);
      },
    };

    if (!this.doesMenuExists(this.navbarMenuTitleAdd, dashboardMenu)) {
      dashboardMenu.push(this.navbarMenuItemAdd);
    }

    // WIDGET_LOCK
    this.navbarMenuItemLock = {
      pluginId: pluginConfig._id,
      pluginName: pluginConfig.name,
      pluginModule: this.pluginModuleTitle,
      nameIdentifier: this.navbarMenuTitleLock,
      accessRights: menuItemsPermissionsRefs,
      primary: {
        enabled: primaryEnabled,
        position: dashboardMenu.length + 1,
      },
      secondary: {
        enabled: secondaryEnabled,
        position: dashboardMenu.length + 1,
      },
      strictMode: true,
      headerSortOrder: 25,
      icon: faLock,
      altIconActive: this.widgetDataStore.edit$,
      altIcon: faLockOpen,
      altName: "WIDGET_UNLOCK",
      dropdown: false,
      disabled: new BehaviorSubject(false),
      callback: async () => {
        this.widgetDataStore.edit$.next(
          !this.widgetDataStore.edit$.value,
        );
      },
    };

    if (!this.doesMenuExists(this.navbarMenuTitleLock, dashboardMenu)) {
      dashboardMenu.push(this.navbarMenuItemLock);
    }

    return dashboardMenu;
  }

  private getMenuItemPermissionRefs(): string[] {
    const menuItemsPermissionsRefs: string[] = [];

    for (const menuItemFlag in this.menuItemPermissions) {
      menuItemsPermissionsRefs.push(
        ...Object.keys(this.menuItemPermissions[menuItemFlag]),
      );
    }

    return menuItemsPermissionsRefs;
  }

  getMenuItemPermissions(): IMenuItemPermissions {
    return this.menuItemPermissions;
  }

  private async openMenuDialog(
    plugin_id: string,
    dialog: MatDialog,
    id?: string,
  ): Promise<void> {
    const lazyComponent = await import(
      "./navbar-menu-item/navbar-menu-item.component"
    );
    const dialogConfig = this.pluginInjectorService.getMenuDialogConfig();
    dialog.open(lazyComponent.NavbarMenuItemComponent, {
      ...dialogConfig,
      panelClass: ["adx-plugin-default-dialog-content", "adx-dialog-large"],
      data: {
        id: id,
        plugin_id: plugin_id,
      },
    });
  }

  private doesMenuExists(
    menuNameIdentifier: string,
    dashboardMenu: IAppDashboardMenu[],
  ): boolean {
    return dashboardMenu.some(
      (menuItem: IAppDashboardMenu) =>
        menuItem.nameIdentifier.toLowerCase() ===
        menuNameIdentifier.toLowerCase(),
    );
  }
}
